import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef} from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import tw from 'tailwind-styled-components'
import {Debouncer} from 'yougile-util'
import {Table} from '@components/common/base/table'
import {PanelResizer} from '@components/right-panel/panel-resizer/panel-resizer'
import {RowData} from '@components/timeline-new/types'
import {resizeStore, timelineResizeStore} from '../resize-store'
import {useTimelineNewStore, virtualize} from '../timeline-new-store'
import {
  assignedColWidth,
  tableGaps,
  useOtherColumnsWidth
} from './columns/title-header'

const TwTasks = tw.div`
  relative
  flex
  flex-col
`

export interface TasksProps {
  className?: string
}
const debouncer = new Debouncer(5)

export const Tasks = observer((props: TasksProps) => {
  const {className} = props
  const [state, store] = useTimelineNewStore()
  const otherColumnsWidth = useOtherColumnsWidth()

  const perfectScrollbarRef = useRef<ScrollBar>(null)
  const initialWidth = timelineResizeStore.getTasksWidth()

  useEffect(() => {
    if (!resizeStore.twTaskRef) {
      return
    }
    resizeStore.twTaskRef.style.width = initialWidth + 'px'
    perfectScrollbarRef.current?.updateScroll()
  }, [resizeStore.twTaskRef])

  const onStartResize = () => {
    state.tableScrollableEl?.classList.add('hide-rails')
  }

  const onResize = (width: number) => {
    debouncer.runButNotOften(() => {
      if (!resizeStore.twTaskRef) {
        return
      }
      resizeStore.twTaskRef.style.width = width + 'px'
      perfectScrollbarRef.current?.updateScroll()

      const newTitleWidth = width - assignedColWidth - tableGaps
      const currentTitleWidth = timelineResizeStore.getTitleWidth()
      if (newTitleWidth < currentTitleWidth) {
        timelineResizeStore.setTitleWidth(newTitleWidth)
      }

      if (width > currentTitleWidth + otherColumnsWidth) {
        timelineResizeStore.setTitleWidth(width - otherColumnsWidth)
      }
    })
  }

  const onEndResize = (width: number) => {
    state.tableScrollableEl?.classList.remove('hide-rails')

    timelineResizeStore.setTasksWidth(width)

    perfectScrollbarRef.current?.updateScroll()
    resizeStore.intermediateTitleWidth = 0

    const newTitleWidth = width - assignedColWidth - tableGaps
    const currentTitleWidth = timelineResizeStore.getTitleWidth()
    if (newTitleWidth < currentTitleWidth) {
      timelineResizeStore.setTitleWidth(newTitleWidth)
    }

    if (width > currentTitleWidth + otherColumnsWidth) {
      timelineResizeStore.setTitleWidth(width - otherColumnsWidth)
    }
  }

  return (
    <TwTasks className={className} ref={resizeStore.setTwTaskRef}>
      <Table<RowData>
        scrollbarRef={perfectScrollbarRef}
        scrollableRef={store.setTableScrollabledEl}
        rows={state.tableRows}
        columns={store.tableColumns}
        className="flex-1"
        headClassName="!p-8 h-48 gap-8 shadow-border-b-1 shadow-line-base w-fit"
        tableRowClassName="!p-8 "
        tableRowInnerClassName="gap-8 flex"
        draggable
        scrollDirection="xy"
        onScrollY={store.onTableScrollY}
        prepareDragData={store.dragHelper.prepareDragData}
        onDrop={store.dragHelper.onDrop}
        dragType="between"
        withHeaderShadow
        shadowType="sm"
        withDragScroll
        virtualize={virtualize}
        hoveredRow={state.hoveredRowPath}
        onRowHover={store.setHoveredRow}
        // fixes a problem w/ diagram and table scroll not being synced
        forceCustomScroll
      />

      <PanelResizer
        type="right"
        initialWidth={initialWidth}
        minWidth={302}
        maxWidth={714}
        onStartResize={onStartResize}
        onResize={onResize}
        onEndResize={onEndResize}
        dragAllowed={true}
        lineWidth="w-[5px]"
        placeholder
      />
    </TwTasks>
  )
})
